import React from 'react';
import Image from 'next/image';
import { IDynamicImage } from '@/types/contentful';
import { Asset } from 'contentful';

export interface ImageData {
  sysId: string;
  src: string;
  contentType: string;
  alt: string;
  width: number;
  height: number;
  adGroupId?: string;
}

export const getImageData = (asset: Asset): ImageData => {
  const { sys, fields } = asset ?? {};

  const altText =
    (fields?.description && fields.description.trim()) ||
    fields?.title ||
    'Greenlight image';

  return {
    sysId: sys?.id ?? '',
    src: fields?.file?.url ?? '',
    contentType: fields?.file?.contentType ?? '',
    alt: altText,
    width: fields?.file?.details?.image?.width ?? 1,
    height: fields?.file?.details?.image?.height ?? 1
  };
};

export const getImageDataFromOptions = (
  keyword: string,
  channel: string,
  adGroupId: string,
  fallbackImage: Asset,
  options?: IDynamicImage[]
): ImageData => {
  if (!options || !options.length) {
    return getImageData(fallbackImage);
  }

  const variation = options.find(
    ({ fields }: IDynamicImage) =>
      fields.keyword === keyword &&
      (!adGroupId ||
        (fields.adgroupId && fields.adgroupId.includes(adGroupId))) &&
      fields.channels.includes(channel)
  );

  return variation
    ? getImageData(variation?.fields?.image)
    : getImageData(fallbackImage);
};

export const getAssetFromOptions = (
  keyword: string,
  channel: string,
  adGroupId: string,
  fallbackImage: Asset,
  options?: IDynamicImage[]
): Asset => {
  if (!options || !options.length) {
    return fallbackImage;
  }

  const variation = options.find(
    ({ fields }: IDynamicImage) =>
      fields.keyword === keyword &&
      (!adGroupId ||
        (fields.adgroupId && fields.adgroupId.includes(adGroupId))) &&
      fields.channels.includes(channel)
  );

  return (variation?.fields?.image as Asset) ?? fallbackImage;
};

export const getImageDataWithHttps = (image: Asset): ImageData => {
  const imageData = getImageData(image);
  return {
    ...imageData,
    src: `https:${imageData.src}`
  };
};

export const renderUnoptimizedImage = (
  asset: Asset,
  className: string
): React.ReactElement => {
  const imageData = getImageDataWithHttps(asset);
  return React.createElement(Image, {
    src: imageData.src,
    alt: imageData.alt,
    width: imageData.width,
    height: imageData.height,
    unoptimized: true,
    className
  });
};
